// src/components/modals/FileModals/FileUploadModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Alert, Form } from 'react-bootstrap';
import { formatFileSize, generateUniqueFolderName } from '../../../services/file.service';
import { checkStorageLimits } from '../../../services/api.service';
import useModalHistory from '../../../hooks/useModalHistory';
import useFileOperations from '../../../hooks/useFileOperations';

const FileUploadModal = ({ show, onHide, onUpload, currentPath, onFolderCreated }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [isLargeFile, setIsLargeFile] = useState(false);
  const [largeFileInfo, setLargeFileInfo] = useState(null);
  const [uploadType, setUploadType] = useState('files'); // 'files' or 'folder'
  
  // New states for folder creation
  const [showFolderInput, setShowFolderInput] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [folderSuccess, setFolderSuccess] = useState('');

  const [isCheckingStorage, setIsCheckingStorage] = useState(false);

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const folderInputTextRef = useRef(null);

  const token = sessionStorage.getItem("userToken");

  useModalHistory(show, onHide, 'files-upload-modal');

  // Clear files and error on modal open/close
  useEffect(() => {
    if (!show) {
      setFiles([]);
      setError('');
      setIsLargeFile(false);
      setLargeFileInfo(null);
      setUploadType('files');
      setShowFolderInput(false);
      setFolderName('');
      setIsCreatingFolder(false);
      setFolderSuccess('');
      setIsCheckingStorage(false);
    }
  }, [show]);

  // Focus on folder input when it appears
  useEffect(() => {
    if (showFolderInput && folderInputTextRef.current) {
      folderInputTextRef.current.focus();
    }
  }, [showFolderInput]);

  // Handle file input change
  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    
    // Determine if it's a folder upload by checking webkitdirectory property
    const isFolder = e.target.webkitdirectory || 
                     e.target.getAttribute('webkitdirectory') === 'true' ||
                     selectedFiles.some(file => file.webkitRelativePath);
    
    setUploadType(isFolder ? 'folder' : 'files');
    
    // Log folder structure if detected
    if (isFolder) {
      const folderPaths = new Set();
      
      selectedFiles.forEach(file => {
        if (file.webkitRelativePath) {
          const pathParts = file.webkitRelativePath.split('/');
          pathParts.pop(); // Remove filename
          
          let currentPath = '';
          for (const part of pathParts) {
            currentPath = currentPath ? `${currentPath}/${part}` : part;
            folderPaths.add(currentPath);
          }
        }
      });
      
    }

    // Check storage limits for ALL files first
    const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    setIsCheckingStorage(true);
    try {
      const limits = await checkStorageLimits(totalSize, token);
      if (!limits.withinLimits) {
        setError(`Storage limit exceeded. You have ${formatFileSize(limits.remainingSpace)} remaining.`);
        return;
      }
    } catch (err) {
      setError('Failed to check storage limits');
      return;
    } finally {
      setIsCheckingStorage(false);
    }

    // Check for large files after storage validation
    const hasLargeFile = selectedFiles.some(file => file.size > 50 * 1024 * 1024);

    if (hasLargeFile) {
      const largestFile = selectedFiles.reduce((prev, current) => 
        (prev.size > current.size) ? prev : current
      );
      
      setIsLargeFile(true);
      setLargeFileInfo({
        name: largestFile.name,
        size: formatFileSize(largestFile.size)
      });
    } else {
      setIsLargeFile(false);
      setLargeFileInfo(null);
    }

    // Set files only after all validations pass
    setFiles(selectedFiles);
    setError('');
  };

  // Handle drag and drop events
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // Get all the files from the event
    let droppedFiles = [];

    // If there are items with webkitGetAsEntry, it means the user might be dropping a folder
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      // Check if any item has directory property
      let hasFolder = false;
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        const item = e.dataTransfer.items[i];
        if (item.webkitGetAsEntry && item.webkitGetAsEntry().isDirectory) {
          hasFolder = true;
          break;
        }
      }

      // If a folder is detected, inform the user to use the folder selector
      if (hasFolder) {
        setError('For folder uploads, please use the "Select Folder" button.');
        return;
      }
    }

    droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    // Set upload type (always files for drag and drop, folders handled separately)
    setUploadType('files');

    // Check storage limits for ALL files first
    const totalSize = droppedFiles.reduce((sum, file) => sum + file.size, 0);
    
    setIsCheckingStorage(true); 
    try {
      const limits = await checkStorageLimits(totalSize, token);
      if (!limits.withinLimits) {
        setError(`Storage limit exceeded. You have ${formatFileSize(limits.remainingSpace)} remaining.`);
        return;
      }
    } catch (err) {
      setError('Failed to check storage limits');
      return;
    } finally {
      setIsCheckingStorage(false);
    }

    // Check for large files after storage validation
    const hasLargeFile = droppedFiles.some(file => file.size > 50 * 1024 * 1024);

    if (hasLargeFile) {
      const largestFile = droppedFiles.reduce((prev, current) =>
        (prev.size > current.size) ? prev : current
      );

      setIsLargeFile(true);
      setLargeFileInfo({
        name: largestFile.name,
        size: formatFileSize(largestFile.size)
      });
    } else {
      setIsLargeFile(false);
      setLargeFileInfo(null);
    }

    // Set files only after all validations pass
    setFiles(droppedFiles);
    setError('');
  };

  // Handle create folder button click
  const handleCreateFolderClick = () => {
    setShowFolderInput(true);
    setError('');
    setFolderSuccess('');
  };

  // Handle cancel folder creation
  const handleCancelFolderCreation = () => {
    setShowFolderInput(false);
    setFolderName('');
    setError('');
    setFolderSuccess('');
  };

  // Handle folder creation - FIXED VERSION with path capture
  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      setError('Please enter a folder name');
      return;
    }

    setIsCreatingFolder(true);
    setError('');

    try {
      const capturedPath = [...currentPath]; // Create a copy
      const parentPath = capturedPath.length > 0 ? capturedPath.join('/') : '';
      
      // Generate unique folder name
      const uniqueFolderName = await generateUniqueFolderName(folderName.trim(), parentPath);
      
      // Build the full folder path
      const fullFolderPath = parentPath 
        ? `${parentPath}/${uniqueFolderName}`
        : uniqueFolderName;
      
      
      // Import the service function directly and force creation
      const { createFolderInPath } = await import('../../../services/file.service');
      
      // Call the direct creation function (bypasses existence check)
      const success = await createFolderInPath(fullFolderPath);

      if (success) {
        const message = uniqueFolderName !== folderName.trim() 
          ? `Folder "${uniqueFolderName}" created successfully (renamed to avoid conflict)`
          : `Folder "${uniqueFolderName}" created successfully`;
        
        
        if (onFolderCreated) {
          onFolderCreated(capturedPath); // Pass the captured path
        }
        
        // Reset the form state immediately
        setFolderName('');
        setShowFolderInput(false);
        setFolderSuccess('');
        
        setTimeout(() => {
          onHide();
        }, 750); // Slightly longer than the refresh timeout
        
      } else {
        setError('Failed to create folder. Please try again.');
      }
    } catch (err) {
      console.error('Error creating folder:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response?.data
      });
      setError(`Failed to create folder: ${err.message}`);
    } finally {
      setIsCreatingFolder(false);
    }
  };

  // Handle Enter key in folder input
  const handleFolderInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCreateFolder();
    } else if (e.key === 'Escape') {
      handleCancelFolderCreation();
    }
  };

  // Handle upload button click
  const handleUploadClick = () => {
    if (files.length === 0) {
      setError('Please select files to upload');
      return;
    }
    
    // For folder uploads, verify we have webkitRelativePath
    if (uploadType === 'folder') {
      const hasFolderStructure = files.some(file => file.webkitRelativePath);
      if (!hasFolderStructure) {
        console.warn('Folder upload requested but no webkitRelativePath found, switching to regular file upload');
        onUpload(files, 'files');
      } else {
        onUpload(files, 'folder');
      }
    } else {
      // Regular file upload
      onUpload(files, 'files');
    }
  };

  // Get current folder display name
  const getCurrentFolderDisplay = () => {
    if (currentPath.length === 0) {
      return "All Files";
    }
    return currentPath[currentPath.length - 1];
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title id="fileUploadModalLabel">
          Upload to {getCurrentFolderDisplay()}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

         {isCheckingStorage && (
    <Alert variant="info" className="mb-3">
      <i className="fas fa-spinner fa-spin mr-2"></i>
      Checking storage limits...
    </Alert>
  )}

        {folderSuccess && (
          <Alert variant="success" className="mb-3">
            {folderSuccess}
          </Alert>
        )}

        {/* Large File Warning */}
        {isLargeFile && largeFileInfo && (
          <Alert variant="warning" className="mb-3">
            <strong>Large File Detected:</strong> "{largeFileInfo.name}" ({largeFileInfo.size}) is a large file. 
            Upload may take longer than usual.
          </Alert>
        )}

        <div
          id="dragDropArea"
          ref={dropZoneRef}
          className={`drag-drop-area ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: '2px dashed #ccc',
            padding: '2rem',
            borderRadius: '0.5rem',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragging ? '#f8f9fa' : 'transparent',
            marginBottom: '1rem'
          }}
        >
          <div className="text-center">
            <i className="fas fa-cloud-upload-alt mb-3" style={{ fontSize: '3rem', color: '#00CC5C' }}></i>
            <h4>Drag & Drop Files Here</h4>
            <p className="text-muted">or click the buttons below</p>

            <div className="mt-3">
              <Button
                variant="outline-primary"
                className="mr-2"
                onClick={handleCreateFolderClick}
                disabled={showFolderInput || isCreatingFolder}
              >
                <i className="fas fa-folder-plus mr-2"></i>
                Create Folder
              </Button>
              <Button
                variant="outline-primary"
                className="mr-2"
                onClick={() => fileInputRef.current.click()}
              >
                <i className="fas fa-file-upload mr-2"></i>
                Select Files
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => folderInputRef.current.click()}
              >
                <i className="fas fa-folder-open mr-2"></i>
                Select Folder
              </Button>
            </div>

            {/* Folder Creation Input - Only shown when Create Folder is clicked */}
            {showFolderInput && (
              <div className="mt-4 text-left">
                <Form.Label className="text-dark font-weight-bold">Create New Folder:</Form.Label>
                <div className="d-flex">
                  <Form.Control
                    ref={folderInputTextRef}
                    type="text"
                    placeholder="Enter folder name..."
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    onKeyPress={handleFolderInputKeyPress}
                    disabled={isCreatingFolder}
                    className="mr-2"
                  />
                  <Button
                    variant="success"
                    onClick={handleCreateFolder}
                    disabled={isCreatingFolder || !folderName.trim()}
                    className="mr-2"
                  >
                    {isCreatingFolder ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-1"></i>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check mr-1"></i>
                        Create
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleCancelFolderCreation}
                    disabled={isCreatingFolder}
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  Press Enter to create or Escape to cancel
                </Form.Text>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              id="fileInput"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            <input
              ref={folderInputRef}
              type="file"
              id="fileInputFolder"
              webkitdirectory="true"
              directory="true"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {files.length > 0 && (
          <div className="selected-files mt-3">
            <h5 className='text-white'>
              {uploadType === 'folder'
                ? `Selected Folder (${files.length} files)`
                : `Selected Files (${files.length})`}
            </h5>
            <div className="file-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <ul className="list-group">
                {files.slice(0, 20).map((file, index) => (
                  <li
                    key={`file-${index}`}
                    className="list-group-item d-flex justify-content-between align-items-center py-2"
                  >
                    <div className="file-info d-flex align-items-center">
                      <i className="fas fa-file mr-2 text-primary"></i>
                      <span className="text-truncate" style={{ maxWidth: '300px' }}>
                        {uploadType === 'folder' ? file.webkitRelativePath || file.name : file.name}
                      </span>
                    </div>
                    <span className="badge badge-pill badge-light">
                      {formatFileSize(file.size)}
                    </span>
                  </li>
                ))}
                {files.length > 20 && (
                  <li className="list-group-item text-center py-2">
                    <em>...and {files.length - 20} more files</em>
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleUploadClick}
          disabled={files.length === 0}
        >
          Upload {files.length > 0 ? `(${files.length} file${files.length === 1 ? '' : 's'})` : ''}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

FileUploadModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  currentPath: PropTypes.array.isRequired,
  onFolderCreated: PropTypes.func // Now accepts (capturedPath) => void
};

export default FileUploadModal;